import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { AlertTriangle, LogOut } from "lucide-react";
import api from "./api";
import { useAuth } from "./QcApp";

const ROLE_LABELS = {
  admin: "Admin",
  qc: "QC",
  commercial: "Commercial",
  unit_head: "Unit Head",
  accounts: "Accounts",
};

export default function Layout() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [reminders, setReminders] = useState([]);

  useEffect(() => {
    api.get("/dashboard").then((r) => setReminders(r.data.reminders)).catch(() => {});
  }, [location.pathname]);

  const roles = user.roles || [];
  const nav = [
    { to: "/qc/home", label: "Home", show: true },
    { to: "/qc/invoicing", label: "Lab Invoicing", show: true },
    { to: "/qc/vendors", label: "Vendors", show: roles.includes("admin") || roles.includes("unit_head") || roles.includes("accounts") || roles.includes("qc") },
    { to: "/qc/users", label: "Users", show: roles.includes("admin") },
    { to: "/qc/tds", label: "TDS Master", show: roles.includes("admin") },
    { to: "/qc/products", label: "Products", show: roles.includes("admin") },
    { to: "/qc/audit", label: "Audit Log", show: roles.includes("admin") },
    { to: "/qc/settings", label: "Settings", show: true },
  ].filter((n) => n.show);

  return (
    <div className="min-h-screen bg-[#F9F8F6] text-[#1C3F2B]">
      <header className="sticky top-0 z-20 border-b border-[#E8E6E1] bg-white/90 backdrop-blur-md">
        <div className="mx-auto flex max-w-6xl items-center gap-4 px-4 py-3 md:px-6">
          <Link to="/qc/home" className="flex items-center gap-2.5" data-testid="qc-header-home">
            <img src="/snf-logo.png" alt="Sun Nutrafoods" className="h-9 w-auto" />
            <span className="hidden font-display text-lg font-semibold sm:block">Quality Control</span>
          </Link>
          <nav className="flex flex-1 items-center gap-0.5 overflow-x-auto">
            {nav.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={`qc-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `whitespace-nowrap rounded-full px-2.5 py-1.5 text-[13px] font-medium transition-colors ${
                    isActive ? "bg-[#1C3F2B] text-[#F9F8F6]" : "text-[#8A9A91] hover:text-[#1C3F2B]"
                  }`
                }
              >
                {n.label}
              </NavLink>
            ))}
          </nav>
          <div className="hidden items-center gap-2 lg:flex">
            <span className="text-sm font-medium">{user.name}</span>
            {roles.map((r) => (
              <span key={r} className="rounded-full bg-[#1C3F2B]/[0.07] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-[#1C3F2B]">
                {ROLE_LABELS[r]}
              </span>
            ))}
          </div>
          <button
            data-testid="qc-logout"
            onClick={logout}
            className="rounded-full p-2 text-[#8A9A91] transition-colors hover:bg-[#1C3F2B]/5 hover:text-[#1C3F2B]"
            title="Sign out"
          >
            <LogOut className="h-4 w-4" />
          </button>
        </div>
      </header>

      {reminders.length > 0 && (
        <div className="border-b border-[#E5A93D]/40 bg-[#E5A93D]/10" data-testid="qc-reminders">
          <div className="mx-auto max-w-6xl space-y-1.5 px-4 py-3 md:px-6">
            {reminders.map((r) => (
              <Link key={r.case_id + r.message} to={`/qc/cases/${r.case_id}`} className="flex items-center gap-2 text-sm text-[#7a5b12] hover:underline">
                <AlertTriangle className="h-4 w-4 shrink-0" />
                {r.message}
              </Link>
            ))}
          </div>
        </div>
      )}

      <main className="mx-auto max-w-6xl px-4 py-8 md:px-6">
        <Outlet />
      </main>
    </div>
  );
}
